/**
 * GA4 adapter — the ONLY place in the app that touches `window.gtag`
 * (mirrors the single-touchpoint rule for `src/hooks/use-wallet.ts`).
 *
 * GA is bootstrapped in `index.html` with `send_page_view:false`, so this sink
 * OWNS every `page_view` — `page()` is load-bearing, not redundant. Defensive:
 * `gtag` may be absent if the script is blocked, so `isEnabled()` gates the sink
 * and the dispatcher skips it cleanly.
 */
import type { AnalyticsSink, UserTraits } from "../types";

// The single declaration of the gtag global for the whole app — it lives with
// its only consumer (moved here from components/analytics/ga-sync.tsx).
declare global {
	interface Window {
		gtag?: (...args: unknown[]) => void;
	}
}

function gtag(...args: unknown[]): void {
	window.gtag?.(...args);
}

export const googleAnalyticsSink: AnalyticsSink = {
	name: "google-analytics",

	isEnabled() {
		return typeof window !== "undefined" && typeof window.gtag === "function";
	},

	track(event, props) {
		gtag("event", event, props);
	},

	page(path) {
		gtag("event", "page_view", { page_path: path });
	},

	identify(traits: UserTraits) {
		// `user_id` is GA's cross-device join key — an opaque backend id, never
		// PII. Gate on PROD so dev sessions don't pollute the property. See
		// ANALYTICS.md → Privacy.
		if (!import.meta.env.PROD) return;
		if (traits.user_id) {
			gtag("set", { user_id: traits.user_id });
		}
		gtag("set", "user_properties", {
			referred_by_code: traits.referred_by_code ?? null,
			is_new_account: traits.is_new_account ?? null,
		});
	},

	reset() {
		gtag("set", { user_id: null });
	},
};
